import React, { useContext, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import * as Actions from 'actions'
import Button from 'components/base/Button'
import { Box, CircularProgress } from '@material-ui/core'
import { TranslationContext } from 'contexts/TranslationContext'
import { sendWebSocketMessage } from 'middlewares/socketMiddleware'
import { FILE_STATUS, SOURCE_FILE_TYPES, WS_EVENT_TYPES } from 'enums'
import { useAction } from 'hooks'
import * as AuthToken from '~/cookieServices/AuthToken'
import { getSourceFilesGroup } from '~/selectors'
import { formatBytes, generateId } from '~/Util'
import { checkToken } from '~/ServerAPI'
import { isCancelledReq } from '~/errors/handleError'
import { ReactComponent as Success } from '~/assets/projects/import_ready.svg'
import './MobileImport.scss'

const PENDING_STATUS = 'PENDING'
const UPLOAD_STATUS = 'UPLOAD'
const ERROR_STATUS = 'ERROR'
const UPLOAD_FAILED_MESSAGE = "couldn't upload"
const TOKEN_INVALID_MESSAGE = 'import token invalid'
const MOBILE_UPLOAD_MAX_ATTEMPTS = 1

const isTokenError = error => error?.response?.status === 403
const isInvalidTokenCheckError = error => [ 403, 404 ].includes(error?.response?.status)

const DATETIME_OPTIONS = {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
}

const isUploaded = asset => [ FILE_STATUS.PREPARE_THUMBNAILS, FILE_STATUS.READY ]
  .includes(asset.status) && !asset.progress

export const MobileImport = () => {
  const dispatch = useDispatch()
  const location = useLocation()
  const { t } = useContext(TranslationContext)
  const fileInputRef = useRef(null)
  const wakeLockRef = useRef(null)

  const [ , forceUpdate ] = React.useReducer(x => x + 1, 0)
  const [ isTokenChecking, setIsTokenChecking ] = useState(true)
  const [ tokenValidationError, setTokenValidationError ] = useState(null)
  const createUploadFileSession = useAction(Actions.upload.createUploadFileSession)
  const executeUploadFile = useAction(Actions.upload.executeUploadFile)
  const requestDeleteFile = useAction(Actions.sourceFiles.requestDeleteFile)

  const filesRef = useRef(new Map())
  const abortControllersRef = useRef(new Map())

  const { items } = useSelector(state => getSourceFilesGroup(state, SOURCE_FILE_TYPES.MEDIA))

  const pendingValues = Array.from(filesRef.current.values())
  const { uploadedFiles, pendingFiles } = pendingValues.reduce((res, localFile) => {
    const reduxFile = items.find(item => item.id === localFile.id)
    const asset = reduxFile || localFile
    const status = localFile.status === ERROR_STATUS ? ERROR_STATUS : asset.status
    const error = localFile.error || asset.error
    const hasError = status === ERROR_STATUS || !!error?.message
    const fileView = { asset, localFile, status, error }

    if (!hasError && (localFile.uploadCompleted || isUploaded(asset))) {
      res.uploadedFiles.push(fileView)
    } else {
      res.pendingFiles.push(fileView)
    }
    return res
  }, { uploadedFiles: [], pendingFiles: [] })

  uploadedFiles.sort((a, b) => (b.localFile.createLocalTime || 0)
    - (a.localFile.createLocalTime || 0))
  pendingFiles.sort((a, b) => (b.localFile.createLocalTime || 0)
    - (a.localFile.createLocalTime || 0))

  const uploadingFiles = pendingFiles
    .filter(fileView => fileView.status !== ERROR_STATUS && !fileView.error?.message)
  const hasTokenUploadError = pendingFiles
    .some(fileView => fileView.error?.message === TOKEN_INVALID_MESSAGE)
  const allItemsUploaded = uploadedFiles.length > 0 && !pendingFiles.length

  useEffect(() => {
    if (!('wakeLock' in navigator)) return undefined

    let isMounted = true

    const requestWakeLock = async () => {
      try {
        wakeLockRef.current = await navigator.wakeLock.request('screen')
      } catch (err) { /* NOP */ }
    }

    requestWakeLock()

    const onVisibilityChange = () => {
      if (isMounted && document.visibilityState === 'visible') {
        requestWakeLock()
      }
    }

    document.addEventListener('visibilitychange', onVisibilityChange)

    return () => {
      isMounted = false
      document.removeEventListener('visibilitychange', onVisibilityChange)
      if (wakeLockRef.current) {
        wakeLockRef.current.release().catch(() => {})
        wakeLockRef.current = null
      }
    }
  }, [])


  const setFileError = (id, message) => {
    const fileData = filesRef.current.get(id)
    if (!fileData) return

    filesRef.current.set(id, {
      ...fileData,
      uploadCompleted: false,
      status: ERROR_STATUS,
      error: { message },
    })
  }

  const handleUploadError = (id, error) => {
    const controller = abortControllersRef.current.get(id)
    if (controller) {
      controller.abort()
      abortControllersRef.current.delete(id)
    }

    const errorCode = error?.response?.status || error?.code
    const message = isTokenError(error)
      ? TOKEN_INVALID_MESSAGE
      : UPLOAD_FAILED_MESSAGE + (errorCode ? ` (code: ${errorCode})` : '')
    setFileError(id, message)
    forceUpdate()
  }

  const handleButtonClick = e => {
    e.stopPropagation()
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
      fileInputRef.current.click()
    }
  }

  const cancelFileUpload = async asset => {
    const id = typeof asset === 'object' ? asset.id : asset

    if (abortControllersRef.current.has(id)) {
      abortControllersRef.current.get(id).abort()
      abortControllersRef.current.delete(id)
    }

    const fileData = filesRef.current.get(id)
    filesRef.current.delete(id)
    forceUpdate()

    const isTemporaryId = String(id).startsWith('pending-')
    if (!isTemporaryId && fileData && !isUploaded(fileData)) {
      try {
        await requestDeleteFile(id, SOURCE_FILE_TYPES.MEDIA, true)
        dispatch(sendWebSocketMessage({
          type: WS_EVENT_TYPES.USER_BC_DELETE_MEDIA,
          payload: { fileId: id },
        }))
      } catch (error) {
        console.error('Delete failed:', error)
      }
    }
  }

  const uploadFiles = async () => {
    const uploadMap = new Map(
      [ ...filesRef.current ]
        .sort(([ , val1 ], [ , val2 ]) => val2.createLocalTime - val1.createLocalTime)
    )

    for (const [ fileId ] of uploadMap) {
      let currentId = fileId
      const liveFileData = filesRef.current.get(fileId)

      if (!liveFileData || liveFileData.status !== PENDING_STATUS) continue

      const controller = new AbortController()
      abortControllersRef.current.set(currentId, controller)

      try {
        if (!filesRef.current.has(currentId)) continue

        // eslint-disable-next-line no-await-in-loop
        const response = await createUploadFileSession(liveFileData.file,
          SOURCE_FILE_TYPES.MEDIA.toLowerCase(), controller)

        if (!filesRef.current.has(currentId)) continue

        // createUploadFileSession returns err.response instead of throwing.
        if (!response?.data?.id) {
          handleUploadError(currentId, { response })
          continue
        }

        const serverFileId = response.data.id

        abortControllersRef.current.delete(currentId)
        abortControllersRef.current.set(serverFileId, controller)

        const { createLocalTime } = filesRef.current.get(currentId)

        filesRef.current.delete(currentId)
        filesRef.current.set(serverFileId, {
          ...liveFileData,
          id: serverFileId,
          createLocalTime,
          status: UPLOAD_STATUS,
        })

        currentId = serverFileId

        forceUpdate()

        let uploadError = null

        // uploadService reports chunk errors through onError and resolves the promise,
        // so capture the callback error explicitly instead of relying on catch below.
        // eslint-disable-next-line no-await-in-loop
        await executeUploadFile(liveFileData.file, response, error => {
          uploadError = error
        }, controller, MOBILE_UPLOAD_MAX_ATTEMPTS)

        if (uploadError) {
          // uploadService aborts the controller for any terminal upload error.
          // Check cancellation from the error itself, not from controller.signal.
          if (isCancelledReq(uploadError)) continue

          handleUploadError(currentId, uploadError)
          continue
        }

        const uploadedFileData = filesRef.current.get(currentId)
        if (uploadedFileData) {
          filesRef.current.set(currentId, {
            ...uploadedFileData,
            uploadCompleted: true,
            progress: 100,
          })
        }

        forceUpdate()
      } catch (error) {
        if (isCancelledReq(error, controller)) {
          continue
        }

        console.error(`Upload failed for file ${liveFileData.name}, id: ${currentId}`, error)
        handleUploadError(currentId, error)
      } finally {
        if (controller.signal.aborted
          && abortControllersRef.current.get(currentId) === controller) {
          abortControllersRef.current.delete(currentId)
        }
      }
    }
  }

  const onChange = e => {
    if (isTokenChecking || tokenValidationError) return

    const targetFiles = e.target?.files
    if (targetFiles?.length > 0) {
      const filesArr = Array.from(targetFiles)

      filesArr.forEach((file, index) => {
        const id = `pending-${generateId()}`
        const newFile = {
          id,
          file,
          name: file.name,
          size: file.size,
          progress: 0,
          status: PENDING_STATUS,
          createLocalTime: Date.now() + index,
        }
        filesRef.current.set(id, newFile)
      })

      forceUpdate()
      setTimeout(() => {
        uploadFiles()
      })
    }
  }

  useEffect(() => {
    let isMounted = true

    const validateToken = async () => {
      setIsTokenChecking(true)
      setTokenValidationError(null)

      const searchParams = new URLSearchParams(location.search)
      const token = searchParams.get('token')

      if (!token) {
        if (isMounted) {
          setTokenValidationError(TOKEN_INVALID_MESSAGE)
          setIsTokenChecking(false)
        }
        return
      }

      AuthToken.set(token)

      try {
        await checkToken(token)
      } catch (error) {
        if (isMounted) {
          setTokenValidationError(
            isInvalidTokenCheckError(error) ? TOKEN_INVALID_MESSAGE : UPLOAD_FAILED_MESSAGE
          )
        }
      } finally {
        if (isMounted) setIsTokenChecking(false)
      }
    }

    validateToken()

    return () => {
      isMounted = false
    }
  }, [ location.search ])

  useEffect(() => {
    items.forEach(asset => {
      const localFile = filesRef.current.get(asset.id)

      if (!localFile?.uploadCompleted) return

      const isReadyForBroadcast = [
        FILE_STATUS.PREPARE_THUMBNAILS,
        FILE_STATUS.READY,
      ].includes(asset.status)

      if (!isReadyForBroadcast) return

      const controller = abortControllersRef.current.get(asset.id)

      if (!controller) return

      dispatch(sendWebSocketMessage({
        type: WS_EVENT_TYPES.USER_BC_UPLOAD_MEDIA,
        payload: { fileId: asset.id },
      }))

      controller.abort()
      abortControllersRef.current.delete(asset.id)
    })
  }, [ items, dispatch ])

  const isValidationError = !!tokenValidationError
    && (pendingFiles.length + uploadedFiles.length === 0)

  return (
    <Box className="mobile-import-wrapper">
      <div className="mobile-import-close-icon" onClick={() => window.close()}>
        <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="24" height="24">
          <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </div>

      <input
        type="file"
        multiple
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept="*/*"
        onChange={onChange}
      />

      <div className="mobile-import-container">
        <div className="mobile-import-header">
          <div className="mobile-import-title">
            {`${t('MOBILE_UPLOAD_TITLE', 'Upload Media')}`}
          </div>
          {!isValidationError && (
            <p className="mobile-import-subtitle">
              {`${t('MOBILE_UPLOAD_SUBTITLE', 'Select files to upload')}`}
            </p>
          )}
        </div>

        <Choose>
          <When condition={isTokenChecking}>
            <div className="mobile-import-dropzone">
              <CircularProgress size={40} />
            </div>
          </When>

          <When
            condition={isValidationError}
          >
            <div className="mobile-import-dropzone">
              <div style={{ color: '#dc3545', fontWeight: 500 }}>
                {tokenValidationError}
              </div>
            </div>
          </When>

          <When
            condition={!tokenValidationError && (pendingFiles.length + uploadedFiles.length === 0)}
          >
            <div className="mobile-import-dropzone">
              <div onClick={handleButtonClick}>
                <div className="cloud-icon-container">
                  <svg className="cloud-icon" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M46 26C46 17.16 38.84 10 30 10C22.64 10 16.48 14.96 14.6 21.76C8.64 23.12 4 28.48 4 35C4 42.72 10.28 49 18 49H46C52.64 49 58 43.64 58 37C58 30.92 53.48 26.8 46 26Z" />
                    <path d="M44 22C44 13.16 36.84 6 28 6C20.64 6 14.48 10.96 12.6 17.76C6.64 19.12 2 24.48 2 31C2 38.72 8.28 45 16 45H44C50.64 45 56 39.64 56 33C56 26.92 51.48 22.8 44 22Z" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    <path d="M28 35V17M28 17L22 23M28 17L34 23" stroke="#ffffff" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </div>
              <div>
                <Button className="mobile-import-btn" onClick={handleButtonClick}>
                  Add files
                </Button>
              </div>
            </div>
          </When>

          <When condition={pendingFiles.length + uploadedFiles.length > 0}>
            <div className="upload-state-wrapper">
              <If condition={allItemsUploaded}>
                <div className="upload-status-counter">All items uploaded</div>
              </If>
              <If condition={uploadingFiles.length > 0}>
                <div className="upload-status-counter">
                  {`${uploadingFiles.length} item(s) uploading...`}
                </div>
              </If>
              <div style={{ overflowY: 'auto' }}>
                <If condition={pendingFiles.length > 0}>
                  <div className="upload-items-list" style={{ marginBottom: '30px' }}>
                    {pendingFiles.map(fileView => {
                      const { asset, status, error } = fileView
                      const uploaded = isUploaded(asset)
                      const isError = status === ERROR_STATUS || !!error?.message
                      const progressValue = uploaded ? 100 : (asset.progress || 0)
                      const progressBarColor = isError ? '#dc3545' : ''

                      return (
                        <div key={asset.id} className={`upload-item-row ${isError ? 'upload-item-error' : ''}`}>
                          <div className="upload-item-thumbnail">
                            {asset.thumbnail ? (
                              <img src={asset.thumbnail} alt="preview" className="thumbnail" />
                            ) : (
                              <div className="thumbnail-fallback" />
                            )}
                          </div>

                          <div className="upload-item-info">
                            <div className="upload-item-filename">
                              {asset.name || 'File'}
                            </div>
                            <div className="upload-item-progress-container">
                              <div
                                className="upload-item-progress-bar"
                                style={{ width: isError ? '100%' : `${progressValue}%`, backgroundColor: progressBarColor }}
                              />
                            </div>
                            <div className="upload-item-meta">
                              <span>{asset.size ? formatBytes(asset.size) : '0B'}</span>
                              <span>
                                {isError ? (
                                  <span style={{ color: '#dc3545', fontWeight: 500 }}>
                                    {error?.message || UPLOAD_FAILED_MESSAGE}
                                  </span>
                                ) : (
                                  `${progressValue}%`
                                )}
                              </span>
                            </div>
                          </div>

                          {uploaded ? (
                            <div className="upload-item-success-icon">
                              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="24" height="24">
                                <path d="M20 6L9 17l-5-5" stroke="#28a745" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </div>
                          ) : (
                            <button
                              type="button"
                              className="upload-item-cancel-btn"
                              onClick={() => {
                                cancelFileUpload(asset)
                              }}
                            >
                              <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" width="24" height="24">
                                <path d="M18 6L6 18M6 6L18 18" stroke="#c4ccce" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </If>
                <If condition={uploadedFiles.length > 0}>
                  <div className="upload-status-counter">
                    <div className="upload-item-success-icon">
                      <Success />
                    </div>
                    {`${uploadedFiles.length} item(s) uploaded`}
                  </div>

                  <div className="upload-items-list">
                    {uploadedFiles.map(fileView => {
                      const { asset, localFile, status, error } = fileView
                      const isError = status === ERROR_STATUS || !!error?.message

                      return (
                        <div key={asset.id} className={`upload-item-row ${isError ? 'upload-item-error' : ''}`}>
                          <div className="upload-item-thumbnail">
                            {asset.thumbnail ? (
                              <img src={asset.thumbnail} alt="preview" className="thumbnail" />
                            ) : (
                              <div className="thumbnail-fallback" />
                            )}
                          </div>
                          <div className="upload-item-info">
                            <div className="upload-item-filename">
                              {asset.name || 'File'}
                            </div>
                            <div className="upload-item-meta">
                              <span>
                                {asset.size ? formatBytes(asset.size) : '0B'}
                                {`  |  ${new Date(localFile.createLocalTime).toLocaleString(undefined, DATETIME_OPTIONS)}`}
                              </span>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </If>
              </div>
            </div>
            <If condition={!hasTokenUploadError}>
              <div className="bottom-action-container">
                <Button className="mobile-import-btn" onClick={handleButtonClick}>
                  Add more
                </Button>
              </div>
            </If>
          </When>
        </Choose>
      </div>
    </Box>
  )
}
